'use client'

import React from 'react';
import { useEffect, useState } from "react";
import { SpacingType, sectionVariants } from '@/types';
import { Button } from '@/components/ui/button';
import Link from 'next/link';
import { SectionWrapper } from '../components/SectionWraper';
import { EmptyResult } from '../components/EmptyResult';
import parse from 'html-react-parser';
import { cn } from '@/lib/utils';
import { Badge } from '../ui/badge';
import Image from '@/components/ui/Image';
import { ButtonStyleType } from '@/types';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select';
import AOS from 'aos';

// Extend Window interface to include AOS
declare global {
  interface Window {
    AOS: typeof AOS;
  }
}

interface heroContent {
  badge: string;
  title: string;
  content: string;
  microCopy: string;
  hasButton: boolean;
  buttonText: string;
  buttonLink: string;
  buttonStyle: ButtonStyleType;
  secondaryButton: boolean;
  secondaryButtonText: string;
  secondaryButtonLink: string;
  secondaryButtonStyle: ButtonStyleType;
}

interface productData {
  badge: string;
  image: string;
  altText: string;
  title: string;
  content: string;
  link: string;
  features: {
    feature: string;
    icon: string;
  }[];
  filterByBusiness: {
    business: string;
  }[];
  filterByUseCase: {
    useCase: string;
  }[];
}

interface ProductListingProps {  
  spacing?: SpacingType;
  darkMode?: boolean; 
  variant?: sectionVariants;
  usedInPageNav?: boolean;
  pageNavTitle?: string;
  themeChangeOnScroll?: boolean;
  title?: string;
  products?: productData[];
  heroContent?: heroContent;
  attributes?: React.HTMLProps<HTMLElement>;
}

const HeroBlock = ({ contents }: { contents: heroContent }) => {
  return (
    <div className="container"> 
      <div className="flex flex-col items-center gap-6">
        {contents?.badge && (
          <Badge variant="outline" bold>
            {contents?.badge}
          </Badge>
        )}
        {contents?.title && (
          <h1 className="text-center">
            {contents?.title}
          </h1>
        )}
        {contents?.content && (
          <div className="paragraph-lg text-muted-foreground mt-4 text-center max-w-2xl">
            {parse(contents?.content)}
          </div>
        )}
        {contents?.hasButton && (
          <div className="flex flex-col md:flex-row gap-4 mt-2">
            <div className="flex flex-col">
              <Button asChild variant={contents?.buttonStyle} size="lg" className="w-full sm:w-auto">
                <Link href={contents?.buttonLink ?? ""}>
                  {contents?.buttonText}
                </Link>
              </Button>
              {contents?.microCopy && (
                <p className="paragraph-sm text-muted-foreground text-center mt-2">
                  {contents?.microCopy}
                </p>
              )}
            </div>
            {contents?.secondaryButton && (
              <Button asChild variant={contents?.secondaryButtonStyle} size="lg" className="w-full sm:w-auto">
                <Link href={contents?.secondaryButtonLink ?? ""}>
                  {contents?.secondaryButtonText}
                </Link>
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const InputBlock = ({ 
  filterByBusiness, 
  filterByUseCase, 
  selectedBusiness, 
  setSelectedBusiness, 
  selectedUseCase, 
  setSelectedUseCase,
  onFindBestMatch
}: { 
  filterByBusiness: string[], 
  filterByUseCase: string[], 
  selectedBusiness: string, 
  setSelectedBusiness: (value: string) => void, 
  selectedUseCase: string, 
  setSelectedUseCase: (value: string) => void,
  onFindBestMatch: () => void
}) => {
  const selectClass = cn("w-full rounded-sm");
  return (
    <div className="flex gap-10 flex-col lg:flex-row p-6 lg:py-16 lg:px-8 rounded-2xl bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/85 shadow-lg">
      <div className="flex flex-col lg:flex-row flex-1 gap-6 lg:gap-10">
        <SelectGroup className={selectClass}> 
          <Select value={selectedBusiness} onValueChange={(value) => {
            setSelectedBusiness(value);
          }}>
            <SelectLabel>We&apos;re a...</SelectLabel>
            <SelectTrigger className="w-full bg-white">
              <SelectValue placeholder="All Companies" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Companies</SelectItem>
              {filterByBusiness?.map((business: string) => (
                <SelectItem key={business} value={business ?? ""}>
                  {business}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </SelectGroup>
        <SelectGroup className={selectClass}> 
          <Select value={selectedUseCase} onValueChange={(value) => {
            setSelectedUseCase(value);
          }}>
            <SelectLabel>Looking to...</SelectLabel>
            <SelectTrigger className="w-full bg-white">
              <SelectValue placeholder="All Use Cases" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Use Cases</SelectItem>
              {filterByUseCase?.map((useCase: string) => (
                <SelectItem key={useCase} value={useCase ?? ""}>
                  {useCase}
                </SelectItem>
                ))}
              </SelectContent>
            </Select>
        </SelectGroup>
      </div>
      <Button 
        variant="primary" 
        size="lg" 
        className="w-full sm:w-auto self-end h-14 cursor-pointer"
        onClick={onFindBestMatch}
      >
        Find the best match
      </Button>
    </div>
  );
};

const ProductCard = ({ product }: { product: productData }) => {
  return (
    product.link ? (
      <div>
        <Link href={product.link} className='flex flex-col w-full overflow-hidden lg:flex-row border rounded-lg'>
          <div className='w-full lg:w-1/2 flex flex-col gap-8 justify-between p-6 xl:p-10 flex-1 order-2 lg:order-1'>
            <div className='flex flex-col gap-6 flex-1'>
              {product.badge && (  
                <Badge variant="outline" bold>
                  {product.badge}
                </Badge>
              )}
              {product.title && (
                <h3>{product.title}</h3>
              )}
              {product.content && (
                <div className="paragraph-base text-muted-foreground">{parse(product.content)}</div>
              )}
            </div>
            <div className='flex flex-col xl:flex-row gap-6 xl:gap-2 justify-between'>
              {product.features && (
                <div className='flex-col gap-2 hidden md:flex'>
                  {product.features.map((feature, index) => (
                    <div key={index} className='flex items-center gap-3'>
                      <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>
                        {feature.icon}
                      </span>                      
                      <p>{feature.feature}</p>
                    </div>
                  ))}
                </div>
              )}
              {product.link && (
                <Button variant="outline" size="iconLg" grayBorder asChild>
                  <span className="material-symbols-outlined text-foreground group-hover/gallery2:text-primary-foreground">
                    arrow_outward</span>
                </Button>
              )}
            </div>
          </div>
          <div className='w-full shrink lg:w-1/2 order-1 lg:order-2'>
            {product.image && (
              <Image 
                src={product.image} 
                alt={product.altText || product.title} 
                title={product.altText || product.title}
                width={1336} 
                height={1000} 
                className="w-full h-full object-cover aspect-[668/500] lg:rounded-r-lg"
                sizes="(max-width: 1024px) 100vw, 50vw"
              />
            )}
          </div>
        </Link>
      </div>
    ) : (
      <div className='flex flex-col w-full overflow-hidden lg:flex-row border rounded-lg'>
      <div className='w-full lg:w-1/2 flex flex-col gap-8 justify-between p-6 xl:p-10 flex-1 order-2 lg:order-1'>
        <div className='flex flex-col gap-6 flex-1'>
          {product.badge && (  
            <Badge variant="outline">
              {product.badge}
            </Badge>
          )}
          {product.title && (
            <h3>{product.title}</h3>
          )}
          {product.content && (
            <div className="paragraph-base text-muted-foreground">{parse(product.content)}</div>
          )}
        </div>
        <div className='flex flex-col xl:flex-row gap-6 xl:gap-2 justify-between'>
          {product.features && (
            <div className='flex flex-col gap-2'>
              {product.features.map((feature, index) => (
                <div key={index} className='flex items-center gap-3'>
                  <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>
                    {feature.icon}
                  </span>                      
                  <p>{feature.feature}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className='w-full shrink lg:w-1/2 order-1 lg:order-2'>
        {product.image && (
          <Image 
            src={product.image} 
            alt={product.altText || product.title} 
            title={product.altText || product.title}
            width={1336} 
            height={1000} 
            className="w-full h-full object-cover aspect-[668/500] lg:rounded-r-lg"
            sizes="(max-width: 1024px) 100vw, 50vw"
          />
        )}
      </div>
    </div>
    )
  );
};

export function ProductListing({ 
  spacing = 'xlarge', 
  darkMode = false, 
  variant = 'A',  
  usedInPageNav = false, 
  pageNavTitle = 'Products', 
  themeChangeOnScroll = false, 
  title, 
  products,
  heroContent,
  attributes 
}: ProductListingProps) {
  const [filterByBusiness, setFilterByBusiness] = useState<string[]>([]);
  const [filterByUseCase, setFilterByUseCase] = useState<string[]>([]);
  const [selectedBusiness, setSelectedBusiness] = useState<string>("all");
  const [selectedUseCase, setSelectedUseCase] = useState<string>("all");
  const [filteredProducts, setFilteredProducts] = useState<productData[]>([]);
  const [isFiltered, setIsFiltered] = useState<boolean>(false);

  useEffect(() => {
    if (products) {
      // Extract business values from objects and remove duplicates
      const businessValues = products
        .map((product) => product.filterByBusiness)
        .flat()
        .map((item) => item.business)
        .filter((value, index, self) => value && self.indexOf(value) === index);
      
      // Sort business values in specific order: All, micro, small, medium, large, then others
      const businessOrder = ['all', 'micro', 'small', 'medium', 'large'];
      const sortedBusinessValues = businessValues.sort((a, b) => {
        // Extract first word and convert to lowercase for comparison
        const aFirstWord = a.split(' ')[0].toLowerCase();
        const bFirstWord = b.split(' ')[0].toLowerCase();
        
        const aIndex = businessOrder.indexOf(aFirstWord);
        const bIndex = businessOrder.indexOf(bFirstWord);
        
        // If both are in the predefined order, sort by their position
        if (aIndex !== -1 && bIndex !== -1) {
          return aIndex - bIndex;
        }
        // If only a is in the predefined order, a comes first
        if (aIndex !== -1) {
          return -1;
        }
        // If only b is in the predefined order, b comes first
        if (bIndex !== -1) {
          return 1;
        }
        // If neither is in the predefined order, sort alphabetically by first word
        return aFirstWord.localeCompare(bFirstWord);
      });
      
      // Extract useCase values from objects and remove duplicates
      const useCaseValues = products
        .map((product) => product.filterByUseCase)
        .flat()
        .map((item) => item.useCase)
        .filter((value, index, self) => value && self.indexOf(value) === index);
      
      setFilterByBusiness(sortedBusinessValues);
      setFilterByUseCase(useCaseValues);
    }
  }, [products]);

  // Function to filter products based on selected business and use case
  const handleFindBestMatch = () => {
    
    if (!products) {
      setFilteredProducts([]);
      setIsFiltered(false);
      return;
    }

    // If both filters are "all", show all products
    if (selectedBusiness === "all" && selectedUseCase === "all") {
      setFilteredProducts(products);
      setIsFiltered(true);
    } else {
      const filtered = products.filter((product) => {
        // Check business filter
        const businessMatch = selectedBusiness === "all" || 
          product.filterByBusiness.some(item => item.business === selectedBusiness);
        
        // Check use case filter
        const useCaseMatch = selectedUseCase === "all" || 
          product.filterByUseCase.some(item => item.useCase === selectedUseCase);
        
        // Return true only if both conditions are met (AND logic)
        return businessMatch && useCaseMatch;
      });
      
      setFilteredProducts(filtered);
      setIsFiltered(true);
    }

    // Smooth scroll to products section after a short delay to ensure state updates
    setTimeout(() => {
      const productsSection = document.querySelector('[data-scroll-anchor="products-list"]');
      if (productsSection) {
        const rect = productsSection.getBoundingClientRect();
        const scrollTop = window.pageYOffset + rect.top - 200; // 200px offset
        window.scrollTo({
          top: scrollTop,
          behavior: 'smooth'
        });
        
        // Refresh AOS after scrolling to trigger animations for new elements
        setTimeout(() => {
          if (typeof window !== 'undefined' && window.AOS) {
            window.AOS.refresh();
          }
        }, 300);
      }
    }, 100);
  };

  // Reset filters function
  const resetFilters = () => {
    setSelectedBusiness("all");
    setSelectedUseCase("all");
    setFilteredProducts([]);
    setIsFiltered(false);
  };

  return (
    <>
      <SectionWrapper
        usedInPageNav={false}
        pageNavTitle={""}
        themeChangeOnScroll={false}
        spacing={"none"}
        variant={"B"}
        darkMode={false}
        heroSection={false}
        componentName="products-list-section"
        attributes={attributes}
        className="bg-size-[1000px] md:bg-auto"
        style={{ 
          backgroundImage: 'url(/accent-blur.svg)',
          backgroundPosition: 'center bottom',
          backgroundRepeat: 'no-repeat',
        }}
      >
      <SectionWrapper
          usedInPageNav={false}
          pageNavTitle={""}
          themeChangeOnScroll={false}
          spacing={"xlarge"}
          variant={"B"}
          darkMode={false}
          heroSection={false}
          componentName="products-list-hero"
          attributes={attributes}
          className="!bg-transparent"
        >
          <div className="container">
            {heroContent && (
              <HeroBlock contents={heroContent} />
            )}
          </div>
        </SectionWrapper>
        <SectionWrapper
          usedInPageNav={false}
          pageNavTitle={""}
          themeChangeOnScroll={false}
          spacing={"large"}
          variant={"B"}
          darkMode={false}
          heroSection={false}
          componentName="products-list-input"
          className="!pt-0 !bg-transparent"
          attributes={attributes}
        >
          <div className="container">
            <InputBlock 
              filterByBusiness={filterByBusiness} 
              filterByUseCase={filterByUseCase} 
              selectedBusiness={selectedBusiness}
              setSelectedBusiness={setSelectedBusiness}
              selectedUseCase={selectedUseCase}
              setSelectedUseCase={setSelectedUseCase}
              onFindBestMatch={handleFindBestMatch}
            />
          </div>
        </SectionWrapper>
      </SectionWrapper>
      <SectionWrapper
        usedInPageNav={usedInPageNav}
        pageNavTitle={pageNavTitle}
        themeChangeOnScroll={themeChangeOnScroll}
        spacing={spacing}
        variant={variant}
        darkMode={darkMode}
        heroSection={false}
        componentName="products-list"
        attributes={attributes}
      >
        <div className="container">
          <h2 className='text-center mb-10 lg:mb-24' data-scroll-anchor="products-list">
            {title}
          </h2>
          <div className="flex flex-col gap-10">
              {isFiltered ? (
                filteredProducts && filteredProducts.length > 0 ? (
                  filteredProducts.map((item: productData, index: number) => {
                  return (
                      <div key={index} data-aos="fade-up" data-aos-delay={index * 100}>
                        <ProductCard product={item} />                 
                      </div>
                    );
                  })
                ) : (
                  <div className="col-span-2">
                    <EmptyResult
                      className="mt-16"
                      buttonOnClick={resetFilters}
                    />
                  </div>
                )
              ) : (
                products && products.length > 0 ? (
                  products.map((item: productData, index: number) => {
                  return (
                      <div key={index} data-aos="fade-up" data-aos-delay={index * 100}>
                        <ProductCard product={item} />                 
                      </div>
                    );
                  })
                ) : (
                  <div className="col-span-2">
                    <EmptyResult
                      className="mt-16"
                      buttonOnClick={resetFilters}
                    />
                  </div>
                )
              )}
            </div>
        </div>
      </SectionWrapper>
    </>
  );
} 